import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Pagination,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import Grid2 from "@mui/material/Grid2";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import {useTranslation} from "react-i18next";

import {useAlert} from "../context/AlertContext";
import {Course} from "../types";
import {getMyCourses} from "../api/api";
import {themeLogoSvg} from "../helper/logo";

const pageSize = 12;

type CourseWithLogo = Course & {
    logo?: string | null;
};

const createSvgDataUrl = (svg: string) =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const MyCoursesList = () => {
    const {t} = useTranslation();
    const theme = useTheme();

    const [courses, setCourses] = useState<CourseWithLogo[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [reloadKey, setReloadKey] = useState(0);

    const {showAlert} = useAlert();

    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setHasError(false);

        const fetchMyCourses = async () => {
            try {
                const {
                    courses,
                    totalPages,
                } = await getMyCourses(page - 1, pageSize);

                if (cancelled) return;

                setCourses(courses);
                setTotalPages(totalPages || 1);
            } catch (error: any) {
                if (cancelled) return;

                setHasError(true);
                setCourses([]);

                showAlert(
                    error?.message ?? t("mycourses.errors.loadFailedAlert"),
                    "error"
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchMyCourses();

        return () => {
            cancelled = true;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reloadKey]);

    const isInitialLoading = loading && courses.length === 0;

    if (hasError) {
        return (
            <Paper
                elevation={0}
                sx={(theme) => ({
                    mt: 3,
                    p: {xs: 3, md: 4},
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    background:
                        theme.palette.mode === "dark"
                            ? alpha(theme.palette.error.main, 0.08)
                            : alpha(theme.palette.error.main, 0.04),
                })}
            >
                <Stack spacing={2} alignItems="flex-start">
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                        }}
                    >
                        {t("mycourses.errors.loadFailedTitle")}
                    </Typography>

                    <Typography color="text.secondary">
                        {t("mycourses.errors.loadFailedDescription")}
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<ReplayRoundedIcon/>}
                        onClick={() => setReloadKey((value) => value + 1)}
                    >
                        {t("mycourses.retry")}
                    </Button>
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            <Paper
                elevation={0}
                sx={(theme) => ({
                    mb: 3,
                    p: {xs: 2.5, md: 3},
                    borderRadius: 5,
                    overflow: "hidden",
                    position: "relative",
                    border: `1px solid ${theme.palette.divider}`,
                    background:
                        theme.palette.mode === "dark"
                            ? `linear-gradient(
                                135deg,
                                ${alpha(
                                theme.palette.primary.main,
                                0.07
                            )},
                                ${alpha(theme.palette.primary.main, 0.1)}
                            )`
                            : `linear-gradient(
                                135deg,
                                ${alpha(theme.palette.primary.main, 0.09)},
                                ${alpha(theme.palette.primary.main, 0.025)}
                            )`,
                    boxShadow:
                        theme.palette.mode === "dark"
                            ? "0 12px 30px rgba(0,0,0,0.22)"
                            : "0 12px 30px rgba(15,23,42,0.06)",
                })}
            >
                <Box
                    sx={(theme) => ({
                        position: "absolute",
                        width: 220,
                        height: 220,
                        borderRadius: "50%",
                        right: -80,
                        top: -100,
                        background:
                            theme.palette.mode === "dark"
                                ? alpha(
                                    theme.palette.primary.main,
                                    0.07
                                )
                                : alpha(theme.palette.primary.main, 0.12),
                    })}
                />

                <Box
                    sx={(theme) => ({
                        position: "absolute",
                        width: 110,
                        height: 110,
                        borderRadius: "50%",
                        left: -40,
                        bottom: -55,
                        background:
                            theme.palette.mode === "dark"
                                ? alpha(
                                    theme.palette.primary.main,
                                    0.05
                                )
                                : alpha(theme.palette.primary.main, 0.08),
                    })}
                />

                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: {
                            xs: "flex-start",
                            sm: "center",
                        },
                        justifyContent: "space-between",
                        gap: 2,
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                    >
                        <Box
                            sx={(theme) => ({
                                width: 56,
                                height: 56,
                                borderRadius: 4,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color:
                                    theme.palette.primary.main,
                                backgroundColor:
                                    theme.palette.mode === "dark"
                                        ? alpha(
                                            theme.palette.primary.main,
                                            0.1
                                        )
                                        : alpha(
                                            theme.palette.primary.main,
                                            0.1
                                        ),
                                border:
                                    theme.palette.mode === "dark"
                                        ? `1px solid ${alpha(
                                            theme.palette.primary.main,
                                            0.18
                                        )}`
                                        : `1px solid ${alpha(
                                            theme.palette.primary.main,
                                            0.18
                                        )}`,
                            })}
                        >
                            <SchoolRoundedIcon sx={{fontSize: 30}}/>
                        </Box>

                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 900,
                                    lineHeight: 1.2,
                                }}
                            >
                                {t("mycourses.title")}
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 0.5,
                                    color: "text.secondary",
                                    maxWidth: 620,
                                }}
                            >
                                {t("mycourses.subtitle")}
                            </Typography>
                        </Box>
                    </Stack>

                    {loading && (
                        <Chip
                            icon={<CircularProgress size={14}/>}
                            label={t("common.loading")}
                            sx={(theme) => ({
                                fontWeight: 700,
                                borderRadius: 999,
                                backgroundColor:
                                    theme.palette.mode === "dark"
                                        ? alpha(
                                            theme.palette.primary.main,
                                            0.08
                                        )
                                        : theme.palette.background.paper,
                                border:
                                    theme.palette.mode === "dark"
                                        ? `1px solid ${alpha(
                                            theme.palette.primary.main,
                                            0.16
                                        )}`
                                        : `1px solid ${theme.palette.divider}`,
                                "& .MuiChip-icon": {
                                    color:
                                        theme.palette.primary.main,
                                },
                            })}
                        />
                    )}
                </Box>
            </Paper>

            {isInitialLoading ? (
                <Grid2 container spacing={3}>
                    {Array.from({length: 6}).map((_, index) => (
                        <Grid2
                            key={index}
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 4,
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={(theme) => ({
                                    height: 260,
                                    p: 2.75,
                                    borderRadius: 5,
                                    overflow: "hidden",
                                    border: `1px solid ${theme.palette.divider}`,
                                })}
                            >
                                <Skeleton
                                    height={20}
                                    width="28%"
                                    sx={{mb: 1}}
                                />

                                <Skeleton
                                    height={34}
                                    width="76%"
                                />

                                <Skeleton
                                    height={22}
                                    width="90%"
                                    sx={{mt: 2}}
                                />

                                <Skeleton height={22} width="82%"/>
                                <Skeleton height={22} width="65%"/>

                                <Skeleton
                                    height={42}
                                    width="100%"
                                    sx={{mt: 3}}
                                />
                            </Paper>
                        </Grid2>
                    ))}
                </Grid2>
            ) : (
                <Grid2 container spacing={3}>
                    {courses.map((course) => (
                        <Grid2
                            key={course.id}
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 4,
                            }}
                        >
                            <Paper
                                component={Link}
                                to={`/course-details/${course.id}`}
                                elevation={0}
                                sx={(theme) => ({
                                    height: "100%",
                                    minHeight: 260,
                                    display: "flex",
                                    flexDirection: "column",
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 5,
                                    overflow: "hidden",
                                    textDecoration: "none",
                                    color: "inherit",
                                    cursor: "pointer",
                                    position: "relative",
                                    isolation: "isolate",

                                    background:
                                        theme.palette.mode === "dark"
                                            ? `linear-gradient(
                                                135deg,
                                                ${alpha(
                                                theme.palette.primary
                                                    .main,
                                                0.025
                                            )},
                                                ${theme.palette.background.paper}
                                                58%
                                            )`
                                            : `linear-gradient(
                                                135deg,
                                                ${alpha(
                                                theme.palette.primary.main,
                                                0.035
                                            )},
                                                ${theme.palette.background.paper}
                                                58%
                                            )`,

                                    transition:
                                        "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",

                                    boxShadow:
                                        theme.palette.mode === "dark"
                                            ? "0 12px 30px rgba(0,0,0,0.22)"
                                            : "0 12px 30px rgba(15,23,42,0.06)",

                                    "&:hover": {
                                        transform: "translateY(-5px)",
                                        borderColor: alpha(
                                            theme.palette.primary.main,
                                            0.42
                                        ),
                                        boxShadow:
                                            theme.palette.mode === "dark"
                                                ? "0 20px 42px rgba(0,0,0,0.38)"
                                                : "0 20px 42px rgba(15,23,42,0.11)",

                                        "& .course-background-logo": {
                                            opacity:
                                                theme.palette.mode === "dark"
                                                    ? 0.18
                                                    : 0.13,
                                            transform:
                                                "translateY(-50%) scale(1.08)",
                                        },

                                        "& .course-arrow": {
                                            transform:
                                                "translate(3px, -3px)",
                                            color:
                                                theme.palette.primary.main,
                                        },

                                        "& .course-title": {
                                            color:
                                                theme.palette.primary.main,
                                        },
                                    },

                                    "&:focus-visible": {
                                        outline: `2px solid ${theme.palette.primary.main}`,
                                        outlineOffset: 2,
                                    },
                                })}
                            >
                                {course.logo && (
                                    <Box
                                        component="img"
                                        className="course-background-logo"
                                        src={createSvgDataUrl(themeLogoSvg(course.logo, theme))}
                                        alt=""
                                        aria-hidden="true"
                                        sx={(theme) => ({
                                            position: "absolute",
                                            zIndex: -1,
                                            width: {
                                                xs: 190,
                                                sm: 205,
                                            },
                                            height: {
                                                xs: 190,
                                                sm: 205,
                                            },
                                            right: {
                                                xs: -38,
                                                sm: -42,
                                            },
                                            top: "48%",
                                            objectFit: "contain",
                                            opacity:
                                                theme.palette.mode === "dark"
                                                    ? 0.12
                                                    : 0.065,
                                            transform:
                                                "translateY(-50%) scale(1)",
                                            transformOrigin: "center",
                                            pointerEvents: "none",
                                            userSelect: "none",
                                            transition:
                                                "opacity 180ms ease, transform 180ms ease",
                                        })}
                                    />
                                )}

                                <Box
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        px: 2.75,
                                        pt: 2.6,
                                        pb: 2.25,
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        sx={(theme) => ({
                                            mb: 0.75,
                                            width: "fit-content",
                                            fontWeight: 900,
                                            fontSize: 11,
                                            lineHeight: 1.4,
                                            letterSpacing: "0.12em",
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.65
                                                    )
                                                    : alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.78
                                                    ),
                                        })}
                                    >
                                        {t("mycourses.openCourse")}
                                    </Typography>

                                    <Typography
                                        className="course-title"
                                        sx={{
                                            maxWidth: course.logo
                                                ? {
                                                    xs: "75%",
                                                    sm: "72%",
                                                }
                                                : "100%",
                                            minHeight: 48,
                                            fontWeight: 900,
                                            fontSize: 20,
                                            lineHeight: 1.25,
                                            letterSpacing: "-0.02em",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            transition:
                                                "color 180ms ease",
                                        }}
                                    >
                                        {course.title ||
                                            t("mycourses.untitledCourse")}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1.5,
                                            maxWidth: course.logo
                                                ? {
                                                    xs: "78%",
                                                    sm: "74%",
                                                }
                                                : "100%",
                                            minHeight: 64,
                                            color: "text.secondary",
                                            lineHeight: 1.7,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {course.description ||
                                            t("mycourses.noDescription")}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={(theme) => ({
                                        position: "relative",
                                        zIndex: 1,
                                        px: 2.25,
                                        py: 1.4,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        backdropFilter: "blur(4px)",
                                        backgroundColor:
                                            theme.palette.mode === "dark"
                                                ? alpha(
                                                    theme.palette.background
                                                        .paper,
                                                    0.7
                                                )
                                                : alpha(
                                                    theme.palette.background
                                                        .paper,
                                                    0.76
                                                ),
                                    })}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <Box
                                            sx={(theme) => ({
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                boxShadow:
                                                    theme.palette.mode === "dark"
                                                        ? `0 0 0 4px ${alpha(
                                                            theme.palette
                                                                .primary
                                                                .contrastText,
                                                            0.12
                                                        )}`
                                                        : `0 0 0 4px ${alpha(
                                                            theme.palette
                                                                .primary.main,
                                                            0.12
                                                        )}`,
                                            })}
                                        />

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 800,
                                                color: "text.secondary",
                                            }}
                                        >
                                            {t("mycourses.openCourse")}
                                        </Typography>
                                    </Stack>

                                    <ArrowOutwardIcon
                                        className="course-arrow"
                                        sx={(theme) => ({
                                            fontSize: 19,
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.72
                                                    )
                                                    : "text.secondary",
                                            transition:
                                                "transform 180ms ease, color 180ms ease",
                                        })}
                                    />
                                </Box>
                            </Paper>
                        </Grid2>
                    ))}
                </Grid2>
            )}

            {!loading && !hasError && courses.length === 0 && (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        mt: 3,
                        p: {
                            xs: 3,
                            md: 4,
                        },
                        borderRadius: 5,
                        textAlign: "center",
                        border:
                            theme.palette.mode === "dark"
                                ? `1px dashed ${alpha(
                                    theme.palette.primary.main,
                                    0.3
                                )}`
                                : `1px dashed ${alpha(
                                    theme.palette.primary.main,
                                    0.35
                                )}`,
                        background:
                            theme.palette.mode === "dark"
                                ? alpha(
                                    theme.palette.primary.main,
                                    0.04
                                )
                                : alpha(theme.palette.primary.main, 0.035),
                    })}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 900,
                        }}
                    >
                        {t("mycourses.noAssignedCoursesTitle")}
                    </Typography>

                    <Typography
                        sx={{
                            mt: 1,
                        }}
                        color="text.secondary"
                    >
                        {t("mycourses.noAssignedCoursesDescription")}
                    </Typography>
                </Paper>
            )}

            {totalPages > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 4,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                        disabled={loading}
                    />
                </Box>
            )}
        </>
    );
};

export default MyCoursesList;